<template lang="pug">
.reservation-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :filter-included-fields="filterFields"
    :busy="isBusy"
    no-border-collapse
    table-class="basic-table nohead-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません。
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません。

    // セル：予約
    // tableライブラリは使用するが、見た目はtableではないようにする
    template(#cell(reservation)="row")
      .reservation-row(@click="onDetail(row.item)")
        .d-flex.flex-row.align-items-center.mb-1
          .flex-grow-1
            .date-time.d-flex.align-items-center
              .icon-offline.mr-2(v-if="row.item.type == 0")
              .icon-online.mr-2(v-else)
              span.date {{ getDate(row.item) }}
              span （
              span.week(:class="weekCssClass(row.item)") {{ getWeek(row.item) }}
              span ）
              span.time {{ getTime(row.item) }}
            .status.status-no-consented(v-if="row.item.consented == false") 未承諾（{{ getInputType(row.item) }}）
            .status.status-consented(v-else) 承諾済（{{ getInputType(row.item) }}）
            .resident-name {{ row.item.resident.name }}（{{ row.item.resident.room }}）
          .buttons.svg-icon
            .angle-right
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

/**
 * ### 面会予約テーブルコンポーネント
 */
@Component({})
export default class ReservationTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ReservationTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ReservationTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isBusy!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** フィルター対象カラム */
  @Prop()
  filterFields!: string[];

  get _value(): Array<ReservationTableItem> {
    return this.value;
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(item: ReservationTableItem): string {
    return this.$dateUtil.convertNumberToString(item.datetime, "mm月dd日");
  }

  /**
   * ### 曜日を返す
   */
  public getWeek(item: ReservationTableItem): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(item.datetime);
    return this.$dateUtil.getWeekName(dateObject, "ja");
  }

  /**
   * ### 時分を返す
   */
  public getTime(item: ReservationTableItem): string {
    return this.$dateUtil.convertNumberToString(item.datetime, "hh:MM");
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(item: ReservationTableItem): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(item);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * ### 入力種別を返す
   */
  public getInputType(item: ReservationTableItem): string {
    if (item.inputType == 0) {
      return "代理予約";
    } else {
      return "本人予約";
    }
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: ReservationTableItem): boolean {
    if (item.groupId == this.filter) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: ReservationTableItem): void {
    this.$emit("onDetail", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface ReservationTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface ReservationTableItem {
  id: number;
  type: number;
  consented: boolean;
  inputType: number;
  datetime: number;
  resident: { name: string; room: string };
  family: { name: string; relation: string; id: number };
  tel: string;
  status: boolean;
  onlineVisitUrl?: string;
  updated: number;
  groupId: number;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/mobile/_mixin.scss";
@import "../../../styles/mobile/_colors.scss";

.reservation-table-component {
  .svg-icon {
    width: 16px;
    height: 16px;
    margin-left: -6px;
    .angle-right {
      @include svg-icon("../../../assets/angle-right.svg", $font-base, 18px, 18px);
    }
  }
  .icon-offline {
    @include svg-icon("../../../assets/reservation/offline.svg", $font-base, 24px, 24px);
  }
  .icon-online {
    @include svg-icon("../../../assets/reservation/online.svg", $font-base, 24px, 24px);
  }

  .sunday {
    color: $font-sunday;
  }
  .saturday {
    color: $font-saturday;
  }
  .status-consented {
    color: $font-disable;
    font-weight: bold;
  }
  .status-no-consented {
    color: $font-strong;
    font-weight: bold;
  }
  .resident-name {
    font-weight: bold;
  }
  .buttons {
    .angle-right {
      color: $font-base;
    }
  }
}
</style>
